import {EMPTY, Observable, of} from 'rxjs';
import { Inject, Injectable } from '@angular/core';
import { IspmHttpClient } from '@ispm/core/services/ispm-http-client';
import {get, includes, sortBy, values} from 'lodash';
import { FileProcessorOptions, FileUploadService } from '@ispm/shared/services/file-upload-service';
import {catchError, map, shareReplay, take} from "rxjs/operators";
import {NOTIFICATION_MESSAGE} from "@ispm-client/modules/aggregator/services/consts";
import { NotificationMessageService } from '@ispm/core/components/customise-notification/notifications-message.service';
import {CoreActionCreatorService} from "@ispm/core/services/core-action-creator.service";

@Injectable()
export class FileService {
  private fileUploadOptions: any;

  constructor(private _ispmHttpClient: IspmHttpClient,
              private notificationMessageService: NotificationMessageService,
              private notificationMessage: NOTIFICATION_MESSAGE,
              private uploadService: FileUploadService,
              private _coreActionCreator: CoreActionCreatorService) {
  }
  public entityList: Array<any>;

  /** Get file data*/
  public getFileData(file: string): Observable<any> {
    const params = { id: file};
    const req = this._ispmHttpClient.getFile('fileUtil/file', params, null, null, {
      json: 'false',
      responseType: 'arraybuffer'
    }).pipe(
      map(res => {
        return res.body;
      }),
      shareReplay(5),
      catchError(err => {
        console.error('file Content could not be fetched for')
        return EMPTY;
      }));
    const req$ = req;
    req$.publish().connect();
    return req;
  }

  /**
   * Store file upload options
   * @param options
   */
  public setFileUploadOptions(options: any) {
    this.fileUploadOptions = options;
  }

  /**
   * Get file upload options
   */
  public getFileUploadOptions() {
    return this.fileUploadOptions;
  }

  /**
   * Upload file
   * @param file
   */
  public uploadFile(file): Observable<any> {
    if (file) {
      if (!this.validateExtension(file)) {
        return Observable.of({ title: `This file extension is not supported to upload.`});
      }


        const options: FileProcessorOptions = { };

        if (get(this.fileUploadOptions, 'fileExtension').length) {
          options.acceptedFileExtensions = get(this.fileUploadOptions, 'fileExtension');
        }

      const noFriendlyName = !get(this.fileUploadOptions, 'useFriendlyName') ? 'true' : 'false';
      const uploadOptions = {
        processorType: get(this.fileUploadOptions, 'processorType'),
        noFriendlyName: noFriendlyName,
        checksumAlgorithm: get(this.fileUploadOptions, 'checksumAlgorithm'),
        fileProcessorOptions: options
      };


      return this.uploadService.uploadFormData(file, get(this.fileUploadOptions, 'storageName'),
        uploadOptions, false)
        .catch(() =>
          Observable.of({ body: { title: `${get(this.fileUploadOptions, 'processorType')}_server_processing_failure`}})
        );
    }
  }

  /**
   * validate file extension
   * @param file
   */
  public validateExtension(file): boolean {
    let isValid: boolean = false;
    const fileName = file.name;
    const lastIndex = fileName.lastIndexOf('.');
    const extension = fileName.substr(lastIndex);
    const notAllowedExtensions = get(this.fileUploadOptions, 'noSupportedFileExtension');
    if (notAllowedExtensions && includes(notAllowedExtensions, extension)) {
      this.notificationMessageService.error(`${this.notificationMessage.CANNOT_UPLOAD_FILE} ${notAllowedExtensions.join(',')}`);
      return isValid;
    }
    const allowedExtensions = get(this.fileUploadOptions, 'fileExtension');
    if (lastIndex > 0) {
      if (allowedExtensions && allowedExtensions.length > 0) {
        for (const ext of allowedExtensions) {
          if (extension.toUpperCase() === ext.toUpperCase()) {
            isValid = true;
            break;
          }
        }
      } else {
        isValid = true;
      }
    }
    if (!isValid) {
      this.notificationMessageService.error(`${this.notificationMessage.CANNOT_UPLOAD_FILE_EXPECTED_EXTENSION} ${allowedExtensions}`);
    }
    return isValid;
  }

  /**
   * Get file blob from buffer array
   * @param buffer
   * @param type
   */
  public getBlob(buffer, type?: string): Blob {
    if (type) {
      return new Blob([buffer], { type: type});
    }
    return new Blob([buffer]);
  }

  /**
   * Get normalize ranges for NLP highlight on report
   * @param nlpConfig
   * @param docObject
   */
  getNormalizeRanges(nlpConfig, docObject, supportedETypes, isGeneralized = true, nlpType): any[] {
    let ranges = [];
    //Total document length
    let documentTextLen = get(docObject, 'text').length;
    let structurePositions = get(docObject, 'structure',[]);
    let entitiesPositions = get(docObject, 'entities') || [];

    let classificationPositions = get(docObject, 'classifications',[]);
    let classificationMap = new Map<string, object>();
    classificationPositions.map(obj => classificationMap.set(obj.id ,obj));
    let children = new Array();
    let parent = null;
    let entitiesPositionsKey = new Map<string, object>();
    entitiesPositions = sortBy(entitiesPositions, (entity => {
      return entity['start'];
    }));
    entitiesPositions.map(obj => entitiesPositionsKey.set(obj.id, obj));
    let structurePositionsKey = new Map<string, object>();
    structurePositions.map(obj => structurePositionsKey.set(obj.id, obj));
    structurePositions = get(nlpConfig, 'includeStructures') && structurePositions ? structurePositions.filter(item => includes(get(nlpConfig, 'includeStructures'), get(item, 'etype'))) : [];
    classificationPositions = get(nlpConfig, 'includeStructures') && classificationPositions ? classificationPositions.filter(item => includes(get(nlpConfig, 'includeStructures'), get(item, 'etype'))) : [];
    let positions = []
    if(isGeneralized) {
      positions = [...entitiesPositions, ...structurePositions, ...classificationPositions];
    }
    else {
      let entityPerFinding = this.getEntityObject(entitiesPositions);
      positions = [...entityPerFinding, ...structurePositions, ...classificationPositions];
    }
    //look "layout" resource for configured etypes
    classificationMap.forEach(value =>{
      children = get(get(value,"relations"),"children");
      let childrenObj = [];
      parent = get(value, "parent");
      let calStart = get(value,'start');
      let calEnd = get(value,'end');
      let classificationText = get(value,'text');
      let lastClassIndex = 0;
      let lastEtype = null;
      //sorting child text document index to draw sequentially
      children.forEach(child => {
        childrenObj.push(entitiesPositionsKey.get(child));
      });
      //re-arranging based on ASCE order
      childrenObj.sort((a, b) => {return b.start- a.start}).reverse()
      children = [];
      childrenObj.forEach( child=>{
        children.push(child['id']);
      });
      children.forEach(child=>{
        if(entitiesPositionsKey.has(child)){
          let chiStart = get(entitiesPositionsKey.get(child),"start");
          let chiEnd = get(entitiesPositionsKey.get(child),"end");
            // (Total Text length - children start) - (Total Text length - classification start)
            /* example :
            "Calcified lung nodule noted again in the left."
              - "Calcified " ->> 0-9 - (0 to 9 sentence index) and (234 - 243 total text index)
             */
            let splitIndexLeft = Math.abs((documentTextLen - chiStart) - (documentTextLen - calStart));
            //left out text ->> "lung nodule noted again in the left." (10 - 46)
            let splitIndexRight = Math.abs((documentTextLen - calStart) - (documentTextLen - calEnd));
            let splitClasText = classificationText.slice(0,splitIndexLeft);
            //[(left out text) - (child length)] ->> "nodule noted again in the left." (15 - 46)
            classificationText = classificationText.slice(lastClassIndex,splitIndexRight);
            positions.push({
              attributes: get(entitiesPositionsKey.get(child),'attributes'),
              end: chiStart,
              start: calStart,
              etype: "classification_"+get(entitiesPositionsKey.get(child),'etype'),
              relations: get(entitiesPositionsKey.get(child),'relations'),
              id: 'C'+get(entitiesPositionsKey.get(child),'id'),
              text: splitClasText
            });
            // start Classification from child end - next frame
            calStart = chiEnd;
            lastEtype =get(entitiesPositionsKey.get(child),'etype');
        }
      })
      //if left out sentence
      if(classificationText != ''){
        let claObj = {
          attributes: get(value,'attributes'),
          end: calEnd,
          start: calStart,
          etype: "classification_"+lastEtype,
          relations: get(value,"relations"),
          id: 'C'+Math.ceil(positions.length+1),
          text: classificationText
        };
        positions.push(claObj);
      }
    });
    //check if sentence negated or not
    positions.forEach(function (highlightRange) {
      if (highlightRange.text.trim() !== ""
        && (supportedETypes.length > 0 ? supportedETypes.includes(highlightRange.etype) : true)
        && ((get(highlightRange, 'attributes.negated') ? get(highlightRange, 'attributes.negated') === 0 : true) || highlightRange.etype === 'Section')) {
        ranges.push({
          start: highlightRange.start,
          end: highlightRange.end,
          length: highlightRange.end - highlightRange.start,
          etype: highlightRange.etype,
          id: highlightRange.id
        })
      }
    })
    //sort all index to draw sequentially
    ranges = sortBy(ranges, (range => {
      return range['start'];
    }));
    // Filtering of same results which are there in between two or more different e-types
    for (let index = 0; index < ( nlpType.toLowerCase() === 'lung' ? ranges.length - 1 : ranges.length - 2); index++) {
      if ((ranges[index + 1].start >= ranges[index].start && ranges[index + 1].end <= ranges[index].end) ||
          (ranges[index + 1].start === ranges[index].start && ranges[index + 1].end >= ranges[index].end)) {
        ranges[index + 1].length > ranges[index].length ? ranges.splice(index, 1) : ranges.splice(index + 1, 1);
        index = index - 1; // Moving Index to previous index after removing an element inside a array
      }
    }
    return ranges;
  }

  getNormalizeRangeperFinding(nlpConfig, docObject, currentEventFinding, supportedETypes, nlpType): any[] {
    //for each docObject.event search for entity and pass to entitiesPositions
    let currentEvent = docObject.events[currentEventFinding];
    let currentEntries = Object.entries(currentEvent.attributes);
    this.entityList = [];
    this.getEntity(currentEvent.attributes);
    return this.getNormalizeRanges(nlpConfig, docObject, supportedETypes, false, nlpType)
  }

  getEntity(input){
    if(typeof input === 'object' && !Array.isArray(input)){
          for(let key in input){
            if(key !== 'entity'){
              this.getEntity(input[key]);
            }else{
              this.entityList.push(input[key]);
              return;
            }
          }
        }else if(Array.isArray(input)){
        input.forEach(element => {
          if(element.hasOwnProperty('entity')){
            this.entityList.push(element['entity']);
            return;
          }else{
            this.getEntity(element);
          }
        })
      }
  }


  /**
   *
   * @param fileName: string
   * @param fileTypes: object of arrays of supported extensions
   * @param showForbiddenExtension: if true - shows just the reason,
   *                                if false - shows the reason and the list of supported extensions.
   * @returns: If the file extension is not in the list of supported extensions, it is forbidden and the function returns true.
   *           If the extension is supported - returns false.
   */
  isForbiddenFileTypes(fileName: string, fileTypes: {[prop: string]: string[]}, showForbiddenExtension: boolean = true): boolean {
    let flatList: string[] = [];
    let retVal: boolean = false;
    const fileExtension: string = fileName.slice((fileName.lastIndexOf(".") - 1 >>> 0) + 2);

    values(fileTypes).forEach(val => flatList = [...flatList, ...val]);

    if (flatList.length > 0) {
      if (flatList.indexOf(fileExtension) === -1) {
        retVal = true;
        if (showForbiddenExtension) {
          this.notificationMessageService.error(`${this.notificationMessage.FILE_TYPE_NOT_ALLOWED}`);
        } else {
          this.notificationMessageService.error(`${this.notificationMessage.CANNOT_UPLOAD_FILE_EXPECTED_EXTENSION} ${flatList.join(', ')}`);
        }
      }
    }
    return retVal;
  }

  /**
   * @returns: list of file types (extensions) allowed for uploading
   */
  getUploadFileTypes() {
    let env, supportedFiles = {};
    this._coreActionCreator.fetchEnvironmentDetails()
      .pipe(
        take(1),
        catchError(err => {
          console.error('Failed to fetch environment details');
          return of('');
        })
      )
      .subscribe(data => env = data);


    supportedFiles = env && env.supportedUploadFileTypes ? this.getFileTypesFromString(env.supportedUploadFileTypes) : supportedFiles;

    return supportedFiles;
  }

  private getFileTypesFromString(stringFileTypes) {
    let keysArray = [];
    let stringArray = [];
    let contentArray = [];
    let wordsArray = [];
    let finalObject = {};

    if (stringFileTypes.trim() !== '') {
      const fileTypesString = stringFileTypes.split("/");
      for (let i = 0; i < fileTypesString.length; i++) {
        keysArray[i] = fileTypesString[i].split(":")[0];
        stringArray[i] = fileTypesString[i].split(":")[1];
      }

      for (let i = 0; i < stringArray.length; i++) {
        wordsArray = stringArray[i].split(",").map(item => item.trim());
        contentArray[i] = wordsArray;
        finalObject[keysArray[i]] = contentArray[i];
      }
    }
    return finalObject
  }

  private getEntityObject(entitiesPositions: Array<any>) : Array<any>{
    let flattendArr = this.flattenArray(this.entityList);
    let filteredEntity = entitiesPositions.filter(element => flattendArr.includes(element.id))
    return filteredEntity;
  }

  private flattenArray(arr) {
    return arr.reduce((flattenedArr,element) => {
      return flattenedArr.concat(Array.isArray(element)?this.flattenArray(element):element);
    },[]);
  }
}
